import LanIcon from '@mui/icons-material/Lan';
import React, { Component, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import strings from '../Globalization/index';
import { dataStore } from '../Store';
import consts from '../consts';
import Model from '../models/model';
import connectionService from '../services/Connection';
import TaskBluePng from '../styles/img/assets/task blue.png';
import util from '../util/index';
import { useGridBase } from '../components/grid/GridBaseContext';

const { checkUserRoleAccess } = util;
const getLicenseType = () => {
	const { tags } = dataStore.getState().auth || {};
	return !tags?.LicenseAppName?.includes('catviewer');
};
const { Advantage, EndoCabinet, Proconnect, CATSync } = consts.ConnectivityType;
const { NONSTERISSTEAM, NONSTERISHP } = consts.EquipmentFamilyType;
const { AMSCO3052, AMSCO5052, AMSCO7053L, AMSCO7052HP, AMSCO7053HP, AMSCO1215, System1Endo2, PTX } = consts.ModelType;

const isLimitedUser = (allowedRole = ['supervisor']) => {
	const { tags } = dataStore.getState().auth || {};
	return allowedRole?.includes(tags?.RoleName?.toLowerCase());
};

const isReadOnly = (field) => ({ formik }) => {
    const equipmentFamily = formik?.values?.EquipmentFamilyId;
    const isLimited = !isLimitedUser(['steris admin']);
    
    // Get connectivity type without mutating formik values
    const connectivityType = formik.values.EquipmentConnectivityType || formik.values.ConnectivityType;
    
    const canDisable =
        connectivityType === consts.ConnectivityType.CATSync ||
        [
            consts.EquipmentFamily.NONSTERISHP,
            consts.EquipmentFamily.NONSTERISSTEAM,
            consts.EquipmentType.DRYINGCABINET
        ].includes(formik.values.EquipmentTypeId) ||
        equipmentFamily === NONSTERISSTEAM ||
        equipmentFamily === NONSTERISHP;

    if (field === 'AccessLocation') {
        return isLimited || canDisable;
    }
    if (field === 'TransferPdf') {
        return !![NONSTERISSTEAM, NONSTERISHP].includes(equipmentFamily) || isLimited;
    }
    return isLimited;
};
const URLPathRegex =
	/(^(https?|ftp|file)?:\/\/\S+|([A-Za-z]:\\(?:[^\\\/:*?"<>|\r\n]+\\)*[^\\\/:*?"<>|\r\n]+|\/(?:[^\\\/:*?"<>|\r\n]+\/)+))/;
const UNCPathRegex = /^(\\\\|\/\/)[a-zA-Z0-9\.\-_]{1,}(((([\\\/]{1,})?)[a-zA-Z0-9\-_]{1,}){1,})(([\\\/]{1,})?)[\$]{0,1}/; // Captures paths like \\Win-l661f0vvc95\13108764\ or //Win-l661f0vvc95//13108764

const taskIconRenderer = (params) => {
	return (
		<Link sx={{ width: '25px', height: '25px' }} to={`/dashboardtasks?EquipmentId=${params.value}`}>
			<img src={TaskBluePng} alt="Task Icon" />
		</Link>
	);
};

const wellNumber = (value) => (value > 0 ? value : 'NA');

const TestAccessLocationAdornment = ({ formik, readOnly, disabled }) => {
	const [loading,] = useState(false);
	const [color, setColor] = useState('primary');

	const handleOnClick = async () => {
		const { AccessLocation, ModelId, EquipmentConnectivityType, UserName, Password, Domain } = formik?.values || {};

		const formData = new FormData();
		const formFields = {
			action: "otherAction",
			otherAction: "TestAccessLocation",
			accessLocation: AccessLocation,
			modalId: ModelId,
			ConnectivityType: EquipmentConnectivityType,
			UserName,
			Password,
			Domain
		};

		Object.entries(formFields).forEach(([key, value]) => {
			if (value) formData.append(key, value);
		});

		util.fetchRequest({ formData: formData, url: connectionService.EquipmentList }, (response) => {
			if (response.status === 200) {
				response.text().then(function (text) {
					const response = util.unsafeJson(text);
					if (response.success) {
						Swal.fire({
							titleText: strings.Info,
							text: `${response.accessLocation} ${strings.UrlWorkingFine}`,
							icon: 'info',
							confirmButtonText: strings.Ok,
							allowOutsideClick: false
						});
					} else {
						['http', 'ftp'].includes(AccessLocation)
							? Swal.fire({ titleText: strings.Error, text: `connect ECONNREFUSED ${AccessLocation}`, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false })
							: Swal.fire({
								titleText: strings.Error,
								text: `ENOENT: no such file or directory ${AccessLocation}`,
								icon: 'error',
								confirmButtonText: strings.Ok,
								allowOutsideClick: false
							});
					}
				});
			}
		});
	};

	useEffect(() => {
		if (color !== 'primary') {
			setColor('primary');
		}
	}, [formik?.values?.AccessLocation]);

	return (
		<Button
			variant="contained"
			color={color}
			size="small"
			startIcon={<LanIcon />}
			sx={{ width: `8rem !important`, padding: `8px 2px 8px !important`, marginTop: (theme) => theme.spacing(0.5) }}
			onClick={handleOnClick}
			disabled={
				loading || 
				(formik?.values?.AccessLocation.length > 0
					? false
					: !formik?.values?.AccessLocation || readOnly || disabled)
			}
			disableElevation
		>
			{loading ? "Loading…" : strings.TestURL}
		</Button>
	);
};
const validateConnectivityType = ({ formik }) => {
	const connectivityType = formik?.values?.EquipmentConnectivityType;
	return !!(connectivityType === Advantage || connectivityType === EndoCabinet || connectivityType === Proconnect) && util.nonSterisHide(formik);
};

const validateRAERModel = ({ formik }) => {
	// Check if the selected model name is RapidAER using existing regex from consts
	const modelId = formik?.values?.ModelId;
	const modelCombos = formik?.activeRecord?.combos?.Model ?? [];
	
	if (!modelId || !Array.isArray(modelCombos)) {
		return false;
	}
	
	const selectedModel = modelCombos.find((combo) => combo.LookupId === modelId);
	const modelName = selectedModel?.DisplayValue || selectedModel?.ModelName || '';
	return consts.regex.multiScopeFile.test(modelName);
};

const OnModelChange = ({ formik, value }) => {
	formik.setFieldValue('ModelId', value);
	formik.setFieldValue(
		'TransferPdf',
		value === AMSCO3052 ||
		value === AMSCO5052 ||
		value === AMSCO7052HP ||
		value === AMSCO7053L ||
		value === AMSCO7053HP ||
		value === AMSCO1215 ||
		value === System1Endo2 ||
		value === PTX
	);
};

const ViewAttachment = ({ rowId, isFromGrid }) => {
	const [searchParams] = useSearchParams();
	const id = searchParams.get('id') || rowId;
	return (
		<Button
			startIcon={<AttachmentIcon />}
			component={Link}
			to={`/viewAttachments?equipmentId=${id}`}
			variant="contained"
			color="warning"
			size="small"
			sx = {{
				marginLeft: isFromGrid ? '0px' : 'auto'
			}}
		>
			{strings.Attachment}
		</Button>
	);
};

const fetchCombos = async (formData) => {
	const { options, count } = await fetch(connectionService.Combo, {
		method: 'POST',
		body: formData
	}).then((res) => res.json()).then((json) => {
		const { records } = json;
		let options = records?.map((record) => ({
			id: record.LookupId,
			label: record.DisplayValue,
			...record
		}));
		if (!Array.isArray(options)) {
			options = [];
		}
		return { options: options || [], count: options?.length };
	});
	return { options, count };
}

const loadModels = async (deps) => {
	const { EquipmentTypeId } = deps;
	const formData = new FormData();
	formData.append('asArray', '0');
	formData.append('parentField', '');
	formData.append('comboType', 'Model');
	if (EquipmentTypeId) {
		formData.append('parentId', EquipmentTypeId);
	}
	return await fetchCombos(formData);
};

const fetchEquipmentType = async (pram) => {
	const where = !pram?.activeRecord?.id ? { Name: { $exists: true, $in: ['Steam Sterilizer', 'VHP-Sterilizer'] } } : {}
	const formData = new FormData();
	formData.append('comboType', 'EquipmentType');
	formData.append('CustomeWhere', JSON.stringify(where))
	return await fetchCombos(formData);
}

const fetchChemistryDateFormat = async (deps) => {
	const { DateFormatPrefix: DateFormatPrefixId = '' } = deps;
	const formData = new FormData();
	formData.append('comboType', 'ChemistryCycleDateFormat');
	formData.append('ScopeId', DateFormatPrefixId)
	return await fetchCombos(formData);
}

const chemistryCycleDateFormatInputCondition = [
	{
		op: ({ values }) => {
			return values.EquipmentFamilyId === consts.EquipmentFamilyType.ADVANTAGE
		}
	}
];

const CustomCheckBox = () => {
	const { patch, standaloneFilters } = useGridBase();
	const [active, setActive] = useState(true); // default to true
	const [inactive, setInactive] = useState(false);

	useEffect(() => {
		const isActiveFilters = standaloneFilters.isActive;
		if (!isActiveFilters) {
			setActive(true);
			setInactive(false);
			return;
		}
		const filters = Array.isArray(isActiveFilters) ? isActiveFilters : [isActiveFilters];
		const hasTrue = filters.includes('true');
		const hasFalse = filters.includes('false');
		setActive(hasTrue);
		setInactive(hasFalse);
	}, [JSON.stringify(standaloneFilters)]);

	useEffect(() => {
		const bothSame = active === inactive;
		let isActiveValue;
		if (bothSame) {
			isActiveValue = active ? ['true', 'false'] : [];
		} else {
			isActiveValue = active ? 'true' : 'false';
		}
		patch(prev => ({
			standaloneFilters: {
				...prev.standaloneFilters,
				isActive: isActiveValue
			}
		}));
	}, [active, inactive, patch]);

	const handleToggle = (type) => {
		if (type === 'active') setActive(prev => !prev);
		if (type === 'inactive') setInactive(prev => !prev);
	};

	return (
		<Box display="flex" sx={{marginLeft: 'auto'}} gap={2}>
			<FormControlLabel
				control={<Checkbox checked={active} onClick={() => handleToggle('active')} />}
				label={strings.Active}
			/>
			<FormControlLabel
				control={<Checkbox checked={inactive} onClick={() => handleToggle('inactive')} />}
				label={strings.InActive}
			/>
		</Box>
	);
};

const model = new Model({
	name: 'Equipment',
	defaultSort: 'LastConnection DESC',
	formTitleField: 'EquipmentName',
	gridTitle: 'Equipment',
	currentUrl: window.location.origin + window.location.pathname,
	disableDelete: true,
	disableAdd: true,
	showGridRefreshBtn: true,
	gridToolbarCustomActions: [
		{
			Component: CustomCheckBox
		}
	],
	readOnly: ({ location }) => ['/dashboard', '/modalitydashboard'].includes(location?.pathname),
	disableExport: ({ location }) => ['/dashboard', '/modalitydashboard'].includes(location?.pathname),
	disableColumnSelector: ({ location }) => ['/dashboard', '/modalitydashboard'].includes(location?.pathname),
	disableFiltering: ({ location }) => ['/dashboard', '/modalitydashboard'].includes(location?.pathname),
	hideGridFooter: ({ location }) => ['/dashboard', '/modalitydashboard'].includes(location?.pathname),
	onFormLoad: ({ formik, activeRecord }) => {
		const equipmentModel = activeRecord?.record?.ModelId;
		if (activeRecord.record.EquipmentConnectivityType === CATSync) {
			formik.setFieldValue('AccessLocation', '');
		}
		return formik.setFieldValue(
			'TransferPdf',
			equipmentModel === AMSCO3052 ||
			equipmentModel === AMSCO5052 ||
			equipmentModel === AMSCO7052HP ||
			equipmentModel === AMSCO7053L ||
			equipmentModel === AMSCO7053HP ||
			equipmentModel === AMSCO1215 ||
			equipmentModel === System1Endo2 ||
			equipmentModel === PTX
		);
	},
	filterColumns: ({ grid, isFromEquipmentDetail }) => {
		const hideColumns = [];
		if (grid && isFromEquipmentDetail) {
			hideColumns.push('FirmwareRevision', 'WellCount');
		}
		return { hideColumns };
	},
	initialValues: {
		IsActive: true,
		TransferPdf: true,
		LanguageCodeId: '6124ce10677de3668816e646'
	},
	customizeSaveData: (saveData) => {
		if (saveData?.constructor === {}.constructor) {
			if (!saveData.EquipmentConnectivityType) {
				delete saveData.EquipmentConnectivityType;
			}
			if (!saveData.ConnectivityType) {
				delete saveData.ConnectivityType;
			}
		}
		return saveData;
	},
	parseOnSaveError: (error, { values }) => {
		if (typeof error?.info === 'string') {
			if (error?.info.startsWith('LimitException_EquipmentSerial')) {
				error.infoMessage = strings.LimitException_EquipmentSerial?.replace('{0}', values?.SerialNumber || '');
				error.info = 'LimitException_EquipmentSerial';
			}
		}
		return error;
	},
	comboTypes: [
		{ type: 'Location', loaded: true },
		{ type: 'Model', loaded: true },
		{ type: 'EquipmentType', loaded: true },
		{ type: 'ConnectivityType', loaded: true },
		{ type: 'InterfaceType', loaded: true },
		{ type: 'Language', loaded: true },
		{ type: 'EquipmentDateFormat', loaded: true },
		{ type: 'ChemistryCycleDateFormat', loaded: true },
		{ type: 'DateFormatPrefix', loaded: true }
	],
	recordCombosTypes: [
		{ type: 'Location', loaded: true },
		{ type: 'Model', loaded: true },
		{ type: 'EquipmentType', loaded: true },
		{ type: 'ConnectivityType', loaded: true },
		{ type: 'InterfaceType', loaded: true },
		{ type: 'Language', loaded: true },
		{ type: 'EquipmentDateFormat', loaded: true },
		{ type: 'ChemistryCycleDateFormat', loaded: true },
		{ type: 'DateFormatPrefix', loaded: true }
	],
	formGroups: [
		{
			fields: [
				'EquipmentTypeId',
				'ModelId',
				'EquipmentName',
				'SerialNumber',
				'LocationId',
				'MachineNumber',
				'DateFormat'
			]
		},
		{
			fields: [
				'DateFormatPrefix',
				'ChemistryCycleDateFormat'
			],
			fieldProps: {
				inlineLabel: true,
				inlineLabelSize: 4
			}
		},
		{
			fields: [
				'EquipmentConnectivityType',
				'LanguageCodeId',
				'AccessLocation',
				'Domain',
				'UserName',
				'Password',
				'IsActive',
				'TransferPdf',
				'EquipmentImage',
				'CycleToday',
				'PastDue',
				'TaskPastDue',
				'UploadTimeout'
			]
		}
	],
	formProps: {
        enableReinitialize: false, // Prevent form reset on re-render
        validateOnChange: true,
        validateOnBlur: true
    },
	columns: [
		{
			header: 'Task',
			field: 'TaskId',
			type: 'action',
			renderCell: taskIconRenderer,
			filterable: false,
			sortable: false,
			excludeOnGrid: getLicenseType
		},
		{
			header: 'Model',
			field: 'ModelName',
			width: 180,
			formField: 'ModelId',
			lookup: 'Model',
			parseFilterValue: (newValue) => {
				return Array.isArray(newValue) ? newValue.map((entry) => entry.label) : newValue?.label;
			},
			formLookup: loadModels,
			skipEmptyFilters: true,
			readOnly: isReadOnly('ModelId'),
			dependsOn: ['EquipmentTypeId'],
			addable: ({ formik }) =>
				`/modellist?id=new&returnTo=${encodeURIComponent(`/equipmentlist?id=${formik?.values?.id || 'new'}`)}`,
			editable: ({ formik }) =>
				`/modellist?id=${formik.values.ModelId}&returnTo=${encodeURIComponent(`/equipmentlist?id=${formik?.values?.id || 'new'}`)}`,
			required: true,
			autocompleteValueFormatter: (newValue, { formik }) => {
				let val = newValue;
				if (newValue) {
					val = newValue?.id || newValue;
					formik.setFieldValue('ConnectivityType', newValue.ConnectivityType);
					formik.setFieldValue('EquipmentConnectivityType', newValue.ConnectivityType);
					formik.setFieldValue('EquipmentFamilyId', newValue.EquipmentFamilyId);
				}
				return val;
			},
			onChange: OnModelChange,
			disabled: () => !checkUserRoleAccess(['steris admin', 'steris specialist'])
		},
		{
			header: 'EquipmentType',
			field: 'EquipmentType',
			width: 150,
			filterable: true,
			formField: 'EquipmentTypeId',
			parseFilterValue: (newValue) => {
				return Array.isArray(newValue) ? newValue.map((entry) => entry.label) : newValue?.label;
			},
			lookup: 'EquipmentType',
			formLookup: fetchEquipmentType,
			resets: ['TransferPdf'],
			required: true,
			readOnly: isReadOnly('EquipmentTypeId'),
			skipEmptyFilters: true
		},
		{ field: 'EquipmentId', type: !consts.isNodeServer ? 'int' : 'string', width: 250, excludeOnGrid: true },
		{ header: 'EquipmentName', field: 'EquipmentName', width: 180, required: true, maxLength: 50 },
		{
			header: 'SNo',
			field: 'SerialNumber',
			label: 'SerialNumber',
			width: 160,
			required: true,
			maxLength: 100,
			readOnly: isReadOnly('SerialNumber')
		},
		{ header: 'MachineNumber', field: 'MachineNumber', width: 150, maxLength: 100 },
		{
			header: 'LocationName',
			field: 'LocationName',
			width: 150,
			formField: 'LocationId',
			parseFilterValue: (newValue) => {
				return Array.isArray(newValue) ? newValue.map((entry) => entry.label) : newValue?.label;
			},
			lookup: 'Location',
			skipEmptyFilters: true,
			filterable: true,
			required: true
		},
		{ header: 'CycleCount', field: 'TotalCycle', width: 110, type: 'number', isNotformfield: true, align: 'right' },
		{
			header: 'AccessLocation',
			field: 'AccessLocation',
			width: 300,
			formField: "AccessLocation",
			maxLength: 200,
			required: [
				{
					field: 'EquipmentFamilyId',
					op: ({ value }) => value !== NONSTERISSTEAM && value !== NONSTERISHP
				},
				{
					field: 'ConnectivityType',
					op: ({ value, values }) => {
						const connectivityType = values.EquipmentConnectivityType || value;
						return connectivityType !== CATSync && connectivityType !== Proconnect;
					}
				}
			],
			excludeOnGrid: ({ location }) => location.pathname?.toLowerCase?.() === '/dashboard',
			validate: yup.string().test('is-url-valid', 'URLValidation', (value, { parent }) => {
				const shouldValidate =
					!isLimitedUser() &&
					parent.ConnectivityType !== consts.ConnectivityType.CATSync &&
					parent.ConnectivityType !== consts.ConnectivityType.Proconnect &&
					![consts.EquipmentFamilyType.NONSTERISHP, consts.EquipmentFamilyType.NONSTERISSTEAM].includes(
						parent.EquipmentFamilyId
					);
				let isValid = !shouldValidate || !value?.length;
				if (shouldValidate && value?.length > 0) {
					isValid = URLPathRegex.test(decodeURIComponent(value)) || UNCPathRegex.test(value);
				}
				return isValid;
			}),
			inputSiblingComponents: [TestAccessLocationAdornment],
			readOnly: isReadOnly('AccessLocation'),
			inputWhen: (({ formik, activeRecord }) => (!!activeRecord.record.id && util.nonSterisHide(formik))),
		},
		{ field: 'FirmwareRevision', width: 160, isNotformfield: true },
		{
			header: 'WellCount',
			field: 'WellCount',
			width: 145,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			valueFormatter: wellNumber,
			isNotformfield: true
		},
		{ field: 'LastConnection', width: 200, type: 'dateTime', filterType: 'date', isLocal: true, isNotformfield: true },
		{ field: 'CycleToday', width: 140, isNotformfield: true, type: 'number', filterable: false, sortable: false },
		{
			header: 'DueCount',
			field: 'PastDue',
			width: 140,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			minValue: 0,
			isNotformfield: true,
			excludeOnGrid: getLicenseType
		},
		{
			header: 'PastDueCount',
			field: 'TaskPastDue',
			width: 150,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			minValue: 0,
			isNotformfield: true,
			excludeOnGrid: getLicenseType
		},
		{
			header: 'DateFormat',
			field: 'DateFormat',
			width: 150,
			lookup: 'EquipmentDateFormat',
			excludeOnGrid: getLicenseType,
			readOnly: isReadOnly('DateFormat'),
			inputWhen: (({ formik, activeRecord }) => (!!activeRecord.record.id && util.nonSterisHide(formik)))
		},
		{
			header: 'ChemistryCycleDateFormat',
			field: 'DateFormatPrefix',
			width: 150,
			lookup: 'DateFormatPrefix',
			placeholder: 'Date Format Prefix',
			excludeOnGrid: true,
			columnSize: 6,
			inputWhen: chemistryCycleDateFormatInputCondition,
			required: chemistryCycleDateFormatInputCondition,
			disabled: () => checkUserRoleAccess(['supervisor', 'steris specialist'])
		},
		{
			field: 'ChemistryCycleDateFormat',
			width: 150,
			formLookup: fetchChemistryDateFormat,
			dependsOn: ['DateFormatPrefix'],
			lookup: 'ChemistryCycleDateFormat',
			excludeOnGrid: true,
			inputWhen: chemistryCycleDateFormatInputCondition,
			required: chemistryCycleDateFormatInputCondition,
			columnSize: 6,
			hideLabel: true,
			disabled: () => checkUserRoleAccess(['supervisor', 'steris specialist'])
		},
		{
			header: 'ConnectivityType',
			field: 'Connectivity',
			formField: 'EquipmentConnectivityType',
			width: 160,
			type: 'string',
			lookup: 'ConnectivityType',
			ignoreLookupOnGridFilter: true,
			readOnly: isReadOnly('Connectivity'),
			required: [
				{
					field: 'ModelId',
					op: ({ value, activeRecord }) => {
						const modelCombos = activeRecord?.combos?.Model ?? [];
						let isRequired = true;
						if (!!value && Array.isArray(modelCombos)) {
							const modelEquipmentFamilyId = modelCombos.find(
								(combo) => combo.LookupId === value
							)?.EquipmentFamilyId;

							isRequired = ![
								consts.EquipmentFamilyType.NONSTERISSTEAM,
								consts.EquipmentFamilyType.NONSTERISHP
							].includes(modelEquipmentFamilyId);
						}

						return isRequired;
					}
				}
			],
			disabled: [
				{
					field: 'ModelId',
					op: ({ value, activeRecord }) => {
						const modelCombos = activeRecord?.combos?.Model ?? [];
						let isDisabled = false;
						if (!!value && Array.isArray(modelCombos)) {
							const modelCombo = modelCombos.find((combo) => combo.LookupId === value);
							const modelEquipmentFamilyId = modelCombo?.EquipmentFamilyId;
							isDisabled = [
								consts.EquipmentFamilyType.NONSTERISSTEAM,
								consts.EquipmentFamilyType.NONSTERISHP
							].includes(modelEquipmentFamilyId);
						}

						return isDisabled;
					}
				}
			],
			inputWhen: (({ formik, activeRecord }) => (!!activeRecord.record.id && util.nonSterisHide(formik))),
			onChange: ({ formik, value }) => {
				if (consts.ConnectivityType.CATSync.includes(value)) {
					formik.setFieldValue('AccessLocation', '');
				}
			}
		},
		{
			header: 'Language',
			field: 'LanguageCodeId',
			width: 90,
			lookup: 'Language',
			excludeOnGrid: true,
			autocompleteValueFormatter: (value) => {
				if (value) {
					if (typeof value === 'object') {
						return value['id'];
					}
					return value;
				}
				return 0;
			},
			inputWhen: [
				{
					op: 'and',
					value: [
						{
							field: 'EquipmentConnectivityType',
							op: 'in',
							value: () => [consts.ConnectivityType.Proconnect, consts.ConnectivityType.CSVConvert]
						}
					]
				}
			]
		},
		{
			header: 'Domain',
			field: 'Domain',
			width: 150,
			excludeOnGrid: true,
			maxLength: 50,
			inputWhen: validateConnectivityType,
			readOnly: isReadOnly('Domain')
		},
		{
			header: 'Username',
			field: 'UserName',
			width: 150,
			type: 'string',
			maxLength: 50,
			excludeOnGrid: true,
			autocompleteValueFormatter: (value) => (value ? value : ''),
			inputWhen: validateConnectivityType,
			readOnly: isReadOnly('UserName')
		},
		{
			header: 'Password',
			field: 'Password',
			width: 150,
			maxLength: 50,
			type: 'password',
			excludeOnGrid: true,
			inputProps: {
				inputProps: { autoComplete: 'none' }
			},
			validate: '',
			autoComplete: 'off',
			autocompleteValueFormatter: (value) => (value ? value : ''),
			inputWhen: validateConnectivityType,
			readOnly: isReadOnly('Password')
		},
		{ header: 'Active', field: 'IsActive', type: 'boolean', width: 130 },
		{
			header: 'TransferPdf',
			field: 'TransferPdf',
			type: 'boolean',
			width: 130,
			disabled: isReadOnly('TransferPdf'),
			readOnly: isReadOnly('TransferPdf'),
			inputWhen: (({ formik, activeRecord }) => (!!activeRecord.record.id && util.nonSterisHide(formik)))
		},
		{
			header: 'Language',
			field: 'LanguageName',
			width: 130,
			lookup: 'Language',
			type: 'string',
			ignoreLookupOnGridFilter: true,
			isNotformfield: true
		},
		{
			header: 'Image',
			field: 'EquipmentImage',
			type: 'file',
			width: 90,
			excludeOnGrid: true,
			hideFileName: true,
			accept: 'image/png',
			fileLocation: connectionService.PTXImage
		},
		{
			header: 'Attachment',
			field: 'Attachment',
			width: 200,
			hidden: true,
			renderCell: ({ id }) => {
				return (<ViewAttachment rowId={id} isFromGrid={true} />)
			},
		},
		{
			header: 'UploadTimeout',
			field: 'UploadTimeout',
			type: 'number',
			width: 150,
			minValue: 30,
			maxValue: 300,
			align: 'left',
			headerAlign: 'left',
			excludeOnGrid: true,
			defaultValue: 60,
			inputWhen: ({ formik, activeRecord }) => {
				// Pass both formik and activeRecord to validation function
				return validateRAERModel({ formik: { ...formik, activeRecord } });
			},
			required: ({ activeRecord }) => {
				// Pass activeRecord to validation function  
				return validateRAERModel({ formik: { values: activeRecord || {}, activeRecord } });
			}
		}
	],
	extraExportConfig: {
		PDF: {
			pageSize: 'A2'
		}
	},
	breadcrumbActions: [{component: ViewAttachment}]

});

export { model as equipmentModel };

export default model.createFormBase();
